/**
 * Memoized selector hooks for the Zustand store.
 *
 * Use these instead of `const store = useStore()` to prevent unnecessary re-renders.
 * Each hook subscribes only to the specific slice of state it needs.
 */
import { useStore } from './useStore';
import { useShallow } from 'zustand/react/shallow';
import type { Card, Deck, UserSettings, ReviewHistory } from '../types';
import type { GrammarTopicProgress } from '../types/quiz';
import type { TutorConcept, TutorCurriculumItem, TutorSession, TutorMessage } from '../types/tutor';

// ─── Primitive Selectors ─────────────────────────────────────

export const useCards = (): Card[] => useStore(useShallow((s) => s.cards));
export const useDecks = (): Deck[] => useStore(useShallow((s) => s.decks));
export const useSettings = (): UserSettings => useStore(useShallow((s) => s.settings));
export const useReviewHistory = (): ReviewHistory[] => useStore(useShallow((s) => s.reviewHistory));
export const useDataLoaded = (): boolean => useStore((s) => s.dataLoaded);
export const useIsLoading = (): boolean => useStore((s) => s.isLoading);
export const useCurrentDeckId = (): string => useStore((s) => s.currentDeckId);
export const useGrammarProgress = (): GrammarTopicProgress[] =>
  useStore(useShallow((s) => s.grammarProgress));

// ─── Tutor Selectors ─────────────────────────────────────────

export const useTutorConcepts = (): TutorConcept[] => useStore(useShallow((s) => s.tutorConcepts));
export const useTutorCurriculum = (): TutorCurriculumItem[] =>
  useStore(useShallow((s) => s.curriculum));
export const useCurrentTutorSession = (): TutorSession | null => useStore((s) => s.currentSession);
export const useTutorMessages = (): TutorMessage[] => useStore(useShallow((s) => s.messages));
export const useIsTutorLoading = (): boolean => useStore((s) => s.isTutorLoading);
export const useCurrentExercise = () => useStore((s) => s.currentExercise);

// ─── Action Selectors (stable references) ────────────────────

export const useCardActions = () =>
  useStore(
    useShallow((s) => ({
      addCard: s.addCard,
      updateCard: s.updateCard,
      deleteCard: s.deleteCard,
    })),
  );

export const useDeckActions = () =>
  useStore(
    useShallow((s) => ({
      addDeck: s.addDeck,
      updateDeck: s.updateDeck,
      deleteDeck: s.deleteDeck,
    })),
  );

export const useSettingsActions = () =>
  useStore(
    useShallow((s) => ({
      updateSettings: s.updateSettings,
      completeOnboarding: s.completeOnboarding,
    })),
  );

export const useReviewActions = () =>
  useStore(
    useShallow((s) => ({
      addReviewHistory: s.addReviewHistory,
      removeLastReviewForCard: s.removeLastReviewForCard,
      loadFullReviewHistory: s.loadFullReviewHistory,
    })),
  );

export const useGrammarProgressActions = () =>
  useStore(
    useShallow((s) => ({
      recordQuizResult: s.recordQuizResult,
      getGuideAccuracy: s.getGuideAccuracy,
      getSectionAccuracy: s.getSectionAccuracy,
    })),
  );

export const useTutorActions = () =>
  useStore(
    useShallow((s) => ({
      loadTutorData: s.loadTutorData,
      startSession: s.startSession,
      sendMessage: s.sendMessage,
      submitExercise: s.submitExercise,
      setCurrentExercise: s.setCurrentExercise,
      rateConcept: s.rateConcept,
      getDueConcepts: s.getDueConcepts,
      getNextConcept: s.getNextConcept,
    })),
  );

// ─── Core Actions ───────────────────────────────────────────

export const useRefreshFromApi = () => useStore((s) => s.refreshFromApi);
export const useResetStore = () => useStore((s) => s.resetStore);

// ─── Derived Selectors ───────────────────────────────────────

/** Get cards due for review, filtered by current language pair. */
export const useDueCards = (deckId?: string): Card[] => {
  return useStore(useShallow((s) => s.getDueCards(deckId)));
};

/** Get new (unreviewed) cards, filtered by current language pair. */
export const useNewCards = (deckId?: string, limit?: number): Card[] => {
  return useStore(useShallow((s) => s.getNewCards(deckId, limit)));
};

/** The cards Review can start with today, using the store's real queue rules. */
export const useReviewableCards = (deckId?: string): Card[] =>
  useStore(
    useShallow((s) => [
      ...s.getDueCards(deckId),
      ...s.getNewCards(deckId, s.settings.dailyNewCardLimit ?? 20),
    ]),
  );

/** Count the cards the global Review navigation can start with today. */
export const useReviewableCardCount = (): number => useReviewableCards().length;
